import React, { useCallback, createContext, useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'

import { usePanelStack } from '../../PanelStack/PanelProvider'
import { useFormik, FormikProvider } from '../../FormikProvider/FormikProvider'
import { modifierFormHelpers } from '../modifier-form-helpers'
import { getIn } from 'formik'
import { ModifierBody } from '../ModifierBody/ModifierBody'

const NestedModifiersContext = createContext({})
const PUSH_ACTION = 'push'
const POP_ACTION = 'pop'

export const NestedModifiersProvider = ({ children }) => {
  const clearedNestedModifierState = () => { return {modifier: null} }
  const [nestedModifierState, setNestedModifierState] = useState(clearedNestedModifierState())
  const { modifier, action } = nestedModifierState

  const [nestedModifierIsValid, setNestedModifierIsValid] = useState(false)

  const [modalLoading, setModalLoading] = useState(false)

  const { push, pop } = usePanelStack()
  const { setFieldValue, values } = useFormik()

  const pushNestedModifier = useCallback((modifier) => {
    modifier.isCurrent = true

    const parentModifier = modifier ? modifier.parentModifier : null
    if (parentModifier) parentModifier.isCurrent = false

    return setNestedModifierState({modifier, action: PUSH_ACTION})
  }, [setNestedModifierState])

  const popNestedModifier = useCallback(() => {
    if (modifier) modifier.isCurrent = false

    const parentModifier = modifier ? modifier.parentModifier : null
    if (parentModifier) parentModifier.isCurrent = true

    pop()
    return setNestedModifierState({modifier: parentModifier,
      action: POP_ACTION})
  }, [pop, modifier])

  const recordCurrentSelections = useCallback((modifier) => {
    // This is required to set the previous model changes that occured
    // in formik.
    setFieldValue(modifier.deepFieldNameByGuid || '', modifier.formikValues)
  }, [setFieldValue])

  const confirmNestedModifierSelections = useCallback(() => {
    modifier.setSelected(setFieldValue, true, true)

    recordCurrentSelections(modifier)
    popNestedModifier()
  }, [popNestedModifier, setFieldValue, modifier, recordCurrentSelections])

  // Create a new inner component to make sure it rerenders with the values from
  // this scope.
  const InnerPanelForm = ({ modifier, validate, validationSchema, getSelectedNames }) => {
    // This will have the top level formik values so this instance gets
    // updated on pop as opposed to keeping its stored values.
    const { values, setFieldValue } = useFormik()
    const nestedModifierFormikValues = modifier.getIn(values)

    let removeModifier
    const isSelected = getIn(values, modifier.deepValueFieldName)
    if (!modifier.parent.isSingleSelect && isSelected) {
      removeModifier = () => {
        modifier.resetSubTreeDefaults()
        modifier.setSelected(setFieldValue, false, true)
        popNestedModifier()
      }
    }

    // inner panel closure scope
    const recordCurrentInnerPanelSelections = () => {
      recordCurrentSelections(modifier)
    }

    return (<FormikProvider
      initialValues={nestedModifierFormikValues}
      isInitialValid={validate(nestedModifierFormikValues)}
      validate={validate}
      validationSchema={validationSchema}
    >
      <ModifierBody
        isNested
        itemDetails={modifier}
        getSelectedNames={getSelectedNames}
        onRemoveModifier={removeModifier}
        recordCurrentSelections={recordCurrentInnerPanelSelections}
      />
    </FormikProvider>)
  }

  InnerPanelForm.propTypes = {
    modifier: PropTypes.object,
    validate: PropTypes.func,
    validationSchema: PropTypes.object,
    getSelectedNames: PropTypes.func
  }

  useEffect(() => {
    if (modifier && action === PUSH_ACTION) {
      const { schema: validationSchema, getSelectedNames } = modifierFormHelpers(modifier)

      // overwrites the validation callback
      // to also set nestedModfier form values and validity
      const validate = (newValues) => {
        const isValid = validationSchema.isValidSync(newValues)
        modifier.formikValues = newValues
        if (modifier.isCurrent) {
          // only update the valid state if this modifier is the current modifier
          setNestedModifierIsValid(isValid)
        }
        return isValid
      }

      push(
        <InnerPanelForm
          data-testid={`${modifier.guid}_MODIFIER`}
          modifier={modifier}
          validate={validate}
          validationSchema={validationSchema}
          getSelectedNames={getSelectedNames} />
      )
    }
  }, [modifier, action, push, values])

  return (
    <NestedModifiersContext.Provider value={{
      nestedModifier: modifier,
      pushNestedModifier,
      popNestedModifier,
      nestedModifierIsValid,
      confirmNestedModifierSelections,
      modalLoading,
      setModalLoading,
      recordCurrentSelections
    }}>
      {children}
    </NestedModifiersContext.Provider>
  )
}

export const useNestedModifiers = () => useContext(NestedModifiersContext)

NestedModifiersProvider.propTypes = {
  children: PropTypes.node.isRequired
}
